'use client';

import { useState, useTransition } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  CaptchaTokenSetter,
  useCaptchaToken,
} from '@kit/auth/captcha/client';
import { Alert, AlertDescription, AlertTitle } from '@kit/ui/alert';
import { Button } from '@kit/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@kit/ui/form';
import { Input } from '@kit/ui/input';
import { Textarea } from '@kit/ui/textarea';
import { Trans } from '@kit/ui/trans';

import { ContactEmailSchema } from '~/(marketing)/contact/_lib/contact-email.schema';
import { sendContactEmail } from '~/(marketing)/contact/_lib/server/server-actions';

export function ContactForm() {
  const { t } = useTranslation();
  const [pending, startTransition] = useTransition();
  const { captchaToken, resetCaptchaToken } = useCaptchaToken();
  const [isCaptchaInteractive, setIsCaptchaInteractive] = useState(false);

  const [state, setState] = useState({
    success: false,
    error: false,
  });

  const form = useForm({
    resolver: zodResolver(ContactEmailSchema),
    defaultValues: {
      prename: '',
      surname: '',
      email: '',
      subject: '',
      message: '',
    },
  });

  if (state.success) {
    return <SuccessAlert />;
  }

  if (state.error) {
    return <ErrorAlert />;
  }

  return (
    <Form {...form}>
      <form
        className={'nrf-marketing-form flex flex-col space-y-5'}
        onSubmit={form.handleSubmit((data) => {
          if (!captchaToken) {
            return;
          }

          startTransition(async () => {
            try {
              await sendContactEmail({
                ...data,
                captchaToken,
              });

              setState({ success: true, error: false });
            } catch {
              setState({ error: true, success: false });
            } finally {
              resetCaptchaToken();
            }
          });
        })}
      >
        <div className="flex flex-row space-x-5">
          <div className="flex-grow">
            <FormField
              name={'prename'}
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormControl>
                      <Input
                        maxLength={200}
                        {...field}
                        placeholder={t('marketing:contactForm:contactPrename')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          </div>
          <div className="flex-grow">
            <FormField
              name={'surname'}
              render={({ field }) => {
                return (
                  <FormItem>
                    <FormControl>
                      <Input
                        maxLength={200}
                        {...field}
                        placeholder={t('marketing:contactForm:contactSurname')}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                );
              }}
            />
          </div>
        </div>

        <FormField
          name={'email'}
          render={({ field }) => {
            return (
              <FormItem>
                <FormControl>
                  <Input
                    type={'email'}
                    {...field}
                    placeholder={t('marketing:contactForm:contactEmail')}
                  />
                </FormControl>

                <FormMessage />
              </FormItem>
            );
          }}
        />

        <FormField
          name={'subject'}
          render={({ field }) => {
            return (
              <FormItem>
                <FormControl>
                  <Input
                    maxLength={300}
                    {...field}
                    placeholder={t('marketing:contactForm:contactSubject')}
                  />
                </FormControl>

                <FormMessage />
              </FormItem>
            );
          }}
        />

        <FormField
          name={'message'}
          render={({ field }) => {
            return (
              <FormItem>
                <FormControl>
                  <Textarea
                    className={'min-h-52'}
                    maxLength={5000}
                    {...field}
                    placeholder={t('marketing:contactForm:contactMessage')}
                  />
                </FormControl>

                <FormMessage />
              </FormItem>
            );
          }}
        />

        <div className={isCaptchaInteractive ? 'w-full py-2.5' : 'w-full'}>
          <CaptchaTokenSetter
            options={{
              className: 'w-full',
              onBeforeInteractive: () => setIsCaptchaInteractive(true),
              onAfterInteractive: () => setIsCaptchaInteractive(false),
              options: {
                appearance: 'interaction-only',
                size: 'flexible',
              },
            }}
          />
        </div>

        <Button disabled={pending || !captchaToken} type={'submit'} size="lg">
          <Trans i18nKey={'marketing:contactForm:sendMessage'} />
        </Button>
      </form>
    </Form>
  );
}

function SuccessAlert() {
  return (
    <Alert variant={'success'}>
      <AlertTitle>
        <Trans i18nKey={'marketing:contactForm:contactSuccess'} />
      </AlertTitle>

      <AlertDescription>
        <Trans i18nKey={'marketing:contactForm:contactSuccessDescription'} />
      </AlertDescription>
    </Alert>
  );
}

function ErrorAlert() {
  return (
    <Alert variant={'destructive'}>
      <AlertTitle>
        <Trans i18nKey={'marketing:contactForm:contactError'} />
      </AlertTitle>

      <AlertDescription>
        <Trans i18nKey={'marketing:contactForm:contactErrorDescription'} />
      </AlertDescription>
    </Alert>
  );
}
